/**
 * The two free-trial switches (Admin → Plans & trials → Sign-up & trial): whether a
 * new member, and a new Research Analyst, gets a free trial and for how many
 * days. Every trial mention on the public site, the sign-up pages and the
 * sign-in page follows them, from GET /public/trials (cached 60 s on the API).
 *
 * Until the answer arrives the page shows the switches' defaults (member trial
 * on, 7 days; no RA trial: rai.platform.plans.trial_switches), and so does the
 * prerendered HTML, so a first visit's wording doesn't change while the page
 * loads. A visitor's browser remembers the last answer, so a repeat visit
 * shows the current wording at once. The HTML head starts the request before
 * the app loads (index.html, src/api/prefetch.ts).
 */
import { useQuery } from "@tanstack/react-query";

import { request } from "@/api/client";

export interface PublicTrials {
  /** Days of a new member's free trial; null = no member trial. */
  member_trial_days: number | null;
  /** Days of a new Research Analyst's free trial; null = no RA trial. */
  ra_trial_days: number | null;
}

/** Days of each trial, or null when that switch is off. */
export interface Trials {
  member: number | null;
  ra: number | null;
}

export const NO_TRIALS: Trials = { member: null, ra: null };
/** The switches' defaults and live values: shown until the API answers. */
export const DEFAULT_TRIALS: Trials = { member: 7, ra: null };
const KEY = "rai.trials";
/** The query's key (the prerender seeds it for the build's trial templates). */
export const TRIALS_KEY = ["/public/trials", null, null] as const;

/**
 * The switches the server wrote into this page (rai.site.react_pages fills
 * <meta name="rai-trials" content="member,ra">, 0 = off): current at load, so
 * the first render matches the HTML the visitor already sees.
 */
function fromPage(): PublicTrials | undefined {
  if (typeof document === "undefined") return undefined;
  const c = document.querySelector('meta[name="rai-trials"]')?.getAttribute("content");
  const m = c?.match(/^(\d+),(\d+)$/);
  return m ? { member_trial_days: days(Number(m[1])), ra_trial_days: days(Number(m[2])) } : undefined;
}

function days(v: unknown): number | null {
  return typeof v === "number" && v > 0 ? v : null;
}

function remembered(): PublicTrials | undefined {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(KEY);
    if (!raw) return undefined;
    const v = JSON.parse(raw) as Partial<PublicTrials>;
    return { member_trial_days: days(v.member_trial_days), ra_trial_days: days(v.ra_trial_days) };
  } catch {
    return undefined;
  }
}

function remember(v: PublicTrials): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    // private window or blocked storage: the next visit asks again
  }
}

/** The switches, and whether they're the API's answer yet (`known`; not while loading or offline). */
export function useTrialsState(enabled = true): { trials: Trials; known: boolean } {
  const q = useQuery<PublicTrials>({
    enabled,
    queryKey: TRIALS_KEY,
    queryFn: async ({ signal }) => {
      const v = await request<PublicTrials>("get", "/public/trials", { signal });
      remember(v);
      return v;
    },
    placeholderData: () => fromPage() ?? remembered(),
    staleTime: 60_000,
    retry: 1,
  });
  // No answer: the defaults while loading; if the API can't be reached, no trial is promised.
  if (!q.data) return { trials: q.isError ? NO_TRIALS : DEFAULT_TRIALS, known: false };
  return {
    trials: { member: days(q.data.member_trial_days), ra: days(q.data.ra_trial_days) },
    known: q.isSuccess && !q.isPlaceholderData,
  };
}

/** Days of each free trial (null = off); `enabled` false skips the request. */
export function useTrials(enabled = true): Trials {
  return useTrialsState(enabled).trials;
}

/** Days of a new member's free trial, or null when the member trial is off. */
export function useMemberTrial(): number | null {
  return useTrials().member;
}
